<template>
  <article
    v-editable="blok"
    :class="{ 'without-page-banner': !pageBanner }"
    class="article-wrapper"
  >
    <component :is="pageBannerComponent" v-if="pageBanner" :blok="pageBanner" />
    <section v-if="hasRichTextContent" class="block section-md">
      <div class="container">
        <div class="row flex-wrap justify-around">
          <div class="column sm-11 md-5">
            <TagsList
              :audiences="audiences"
              :show-all="true"
              :topics="topics"
              size="lg"
            />
            <ResourcesMeta
              :link="link"
              :organisation="organisation"
              :resource-type="resourceType"
              :states="states"
            />
            <ArticleDownloads :downloads="downloads" />
          </div>
          <div class="column sm-11 md-7 mobile-order-first tablet-order-first">
            <div class="page-content">
              <header v-if="!pageBanner">
                <NavigationBreadcrumbs />
                <h1 v-if="title">
                  {{ title }}
                </h1>
                <h2
                  v-if="hasArticleIntroduction"
                  class="h3 light whitespace-pre-line"
                >
                  {{ introduction }}
                </h2>
              </header>
              <RichTextRenderer :document="richTextContent" />
              <ResourcesCallToAction
                :card-media="cardMedia"
                :link="link"
                :link-label="linkLabel"
              />
              <LazyVisibleArticleToolButtons
                :introduction="introduction"
                :title="title"
                hydrate-on-visible
              />
              <NewsletterPopupTrigger />
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Related content block -->
    <BlockRelatedContent v-if="!customRelatedContent" :blok="blok" />
  </article>
</template>

<script setup lang="ts">
import type { ResourceStoryblok } from '~/types/storyblok'

const props = defineProps<{ blok: ResourceStoryblok }>()
const {
  title,
  pageBanner,
  pageBannerComponent,
  hasRichTextContent,
  downloads,
  richTextContent,
  introduction,
  hasArticleIntroduction,
  audiences,
  topics,
  states,
  resourceType,
  customRelatedContent,
} = useStoryContent(toRefs(props))

const cardMedia = computed(() => props.blok.image)
const organisation = computed(() => props.blok.organisation)
const link = computed(() => props.blok.link)
const linkLabel = computed(() => props.blok.link_label)
</script>

<style lang="less" scoped>
.article-tool-buttons {
  margin: @gutter-xxl 0 0;
  padding-top: @gutter-lg;
  border-top: solid @2px @color-border;

  :deep(.button-group) {
    .justify-center();
  }
}

@media print {
  article {
    section.block {
      .container {
        .row {
          .column:first-child {
            margin-top: @gutter-lg;
            flex-basis: 100%;
            border: solid @2px @color-border;
            padding: @gutter @gutter-sm;

            > * {
              max-width: none;
            }

            :deep(ul.icon-list) {
              li {
                padding: 0;
              }
            }
          }
        }
      }
    }
  }
}
</style>
